'use client';

import { BookOpen, Keyboard, MessageCircle, ScrollText } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BUG_BENCH_PATH } from '@/constants/bug-bench';
import { DOCS_CHANGELOG_URL, DOCS_URL } from '@/constants/docs';
import { isEmbeddablePath } from '@/constants/embed-paths';
import { useAtlasChat } from '@/hooks/use-atlas-chat';
import { shouldEnableKeyboardShortcutsDrawer } from '@/lib/keyboard-shortcuts/registry';
import { isPrV2Route } from '@/lib/pr-v2/route-utils';
import { useKeyboardShortcutsDrawerStore } from '@/lib/stores/keyboard-shortcuts-drawer-store';
import { useAuth } from '@/providers/auth-provider';
export function HelpWidget() {
  const pathname = usePathname() ?? '';
  const {
    isAuthenticated
  } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isOpeningChat, setIsOpeningChat] = useState(false);
  const {
    openChat
  } = useAtlasChat();
  const openKeyboardShortcuts = useKeyboardShortcutsDrawerStore(state => state.openDrawer);
  // KeyboardShortcutsController only mounts inside AuthenticatedFeatures, so
  // the drawer is unavailable on signed-out routes; hide the entry there.
  const showKeyboardShortcuts = isAuthenticated && shouldEnableKeyboardShortcutsDrawer(pathname);
  if (pathname === BUG_BENCH_PATH || isPrV2Route(pathname) || isEmbeddablePath(pathname)) {
    return null;
  }
  const handleLiveChat = async () => {
    setIsMenuOpen(false);
    setIsOpeningChat(true);
    try {
      const didOpen = await openChat();
      if (!didOpen) {
        toast.error('Could not open chat. Please try again in a moment.');
      }
    } catch {
      toast.error('Could not open chat. Please try again in a moment.');
    } finally {
      setIsOpeningChat(false);
    }
  };
  const handleKeyboardShortcuts = () => {
    setIsMenuOpen(false);
    openKeyboardShortcuts();
  };
  return <div className="fixed bottom-6 right-6 z-[2147483647]" data-help-widget>
      <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" className="inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-foreground/15 bg-zinc-900 p-0 text-zinc-200 shadow-md ring-1 ring-white/5 hover:border-foreground/30 hover:bg-zinc-800 hover:text-white" aria-label="Open help menu">
            <span className="text-[17px] font-medium leading-none" aria-hidden="true">
              ?
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="end" className="w-fit min-w-[10.5rem] [&_[role=menuitem]_svg]:text-muted-foreground">
          <DropdownMenuItem asChild>
            <Link href={DOCS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
              <BookOpen className="size-4 shrink-0" aria-hidden="true" />
              Docs
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link href={DOCS_CHANGELOG_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
              <ScrollText className="size-4 shrink-0" aria-hidden="true" />
              Changelog
            </Link>
          </DropdownMenuItem>
          {showKeyboardShortcuts ? <DropdownMenuItem onClick={handleKeyboardShortcuts}>
              <Keyboard className="size-4 shrink-0" aria-hidden="true" />
              Keyboard shortcuts
            </DropdownMenuItem> : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={isOpeningChat} onClick={handleLiveChat}>
            <MessageCircle className="size-4 shrink-0" aria-hidden="true" />
            {isOpeningChat ? 'Opening chat…' : 'Live chat'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>;
}